/**
 * Säljverktyg (Sales Tool) — Super admin page for looking up companies,
 * viewing their Accurator activity, Syna credit data, and platform-wide
 * procurement statistics. Used internally by the sales team.
 */
import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import AppLayout from '../../components/layout/app-layout/app-layout';
import { Card, Text } from '@tremor/react';
import { BarChart } from '../../components/shared/Charts/BarChart/BarChart';
import SummaryBarList from '../../components/shared/Charts/SummaryBarList/SummaryBarList';
import SupplierControls from '../app-add-supplier/supplier-controls';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import Selector from '../../components/shared/Selector/Selector';
import { createFilter } from 'react-select';
import { ServiceSalesTool } from '../../services/service-sales-tool';

import { useHistory } from 'react-router-dom';
import { UNIT } from '../page-dashboard-V2/helper';
import { placeOptions } from '../page-supplier-presentation/PlaceSelector';
import { parseAmount } from '../../util';
import './page-sales-tool.scss';

type TimeRange = 'today' | 'all' | '12m' | '6m' | '3m' | '1m';

const timeRangeLabels: Record<TimeRange, string> = {
  today: 'Just nu',
  all: 'Alla',
  '12m': '12 månader',
  '6m': '6 månader',
  '3m': '3 månader',
  '1m': '1 månad',
};

const getDateRange = (range: TimeRange) => {
  if (range === 'all' || range === 'today') return {};
  const now = new Date();
  const months = parseInt(range);
  const from = new Date(now);
  from.setMonth(from.getMonth() - months);
  return { fromDate: from.toISOString(), toDate: now.toISOString() };
};

// --- Syna XML response parsers ---
// Syna returns XML converted to JSON with _text/_attributes patterns.
// These parsers extract the relevant fields for display.
const getText = (obj: any) => obj?._text || obj?.text || obj || '';

const parseSynaCompanyData = (data: any) => {
  if (!data?.companyData) return null;
  try {
    const omfragad = data.companyData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return null;
    const addrList = omfragad?.Adresslista?.Adress;
    const addr = Array.isArray(addrList) ? addrList[0] : addrList;
    const bolsate = getText(omfragad?.Bolsate);
    return {
      name: getText(omfragad?.Namnlista?.Namn),
      address: getText(addr?.Gatabox),
      postcode: getText(addr?.Postnr),
      city: getText(addr?.Postort),
      kommun: bolsate ? bolsate.split('/')[0].trim() : '',
      ftgform: getText(omfragad?.Ftgform),
      regdatum: getText(omfragad?.Regdatum),
      orgNumber: omfragad?._attributes?.id || '',
      bransch: (() => {
        const bl = omfragad?.Branschlista?.Bransch;
        if (!bl) return '';
        const first = Array.isArray(bl) ? bl[0] : bl;
        return getText(first?.Branschtext);
      })(),
      board: [] as { name: string; role: string }[], // filled from boardData separately
    };
  } catch {
    return null;
  }
};

const parseSynaRating = (data: any) => {
  if (!data?.ratingData) return null;
  try {
    const omfragad = data.ratingData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return null;
    const klass = omfragad?.Bedomning?.Klassning?.Klass;
    return {
      rating: getText(klass),
      ratingText: klass?._attributes?.not || '',
      limit: getText(omfragad?.Limit),
    };
  } catch {
    return null;
  }
};

const parseSynaBoardData = (data: any): { name: string; role: string }[] => {
  try {
    const omfragad = data?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return [];
    const ledamotList = omfragad?.Ledningslista?.Ledamot;
    if (!ledamotList) return [];
    const list = Array.isArray(ledamotList) ? ledamotList : [ledamotList];
    return list.map((l: any) => {
      const funcs = l?.Funktionslista?.Funktion;
      let role = '';
      if (Array.isArray(funcs)) {
        role = funcs.map((f: any) => f?._attributes?.klartext || '').filter(Boolean).join(', ');
      } else {
        role = funcs?._attributes?.klartext || '';
      }
      // Shorten common role names
      role = role.replace(/Verkställande direktör/gi, 'VD');
      return {
        name: getText(l?.Lednamn) || '',
        role,
      };
    }).filter((m: any) => {
      // Exclude auditors (revisorer) — only show board members and executives
      if (!m.name) return false;
      return !m.role.toLowerCase().includes('revisor');
    });
  } catch {
    return [];
  }
};

interface KPIYear {
  period: string;
  omsattning: number;
  resultat: number;
  balansomslutning: number;
  egetKapital: number;
  soliditet: string;
  anstallda: number;
  kassalikviditet: string;
}

/** Parse Swedish-formatted numbers (e.g. "1 234,56" or "*" for missing data) */
const parseSwedishNumber = (val: string | undefined): number => {
  if (!val || val === '*') return 0;
  return parseAmount(val);
};

const parseSynaKPIs = (data: any): KPIYear[] => {
  try {
    const omfragad = data?.kpiData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return [];
    const nyckeltal = omfragad?.Nyckeltalslista?.Nyckeltal;
    if (!nyckeltal) return [];
    const list = Array.isArray(nyckeltal) ? nyckeltal : [nyckeltal];
    return list.map((n: any) => {
      const periodFrom = n?.Period_fr?._text || '';
      const periodTo = n?.Period_to?._text || '';
      const year = periodTo ? `20${periodTo.substring(0, 2)}` : '';
      const month = periodTo ? periodTo.substring(2) : '';
      return {
        period: year && month ? `${year}-${month}` : periodFrom + '-' + periodTo,
        omsattning: parseSwedishNumber(n?.Oms?._text),
        resultat: parseSwedishNumber(n?.Res?._text),
        balansomslutning: parseSwedishNumber(n?.BalOmsl?._text),
        egetKapital: parseSwedishNumber(n?.EgetKap?._text),
        soliditet: n?.Soliditet?._text || '-',
        anstallda: parseSwedishNumber(n?.Anst?._text),
        kassalikviditet: n?.Kassalikv?._text || '-',
      };
    });
  } catch {
    return [];
  }
};

const subscriptionLabels: Record<string, string> = {
  RECOMMENDED: 'Prio',
  ALL_QUOTATIONS: 'Kontroll',
  OPEN_QUOTATIONS: 'Publicerade förfrågningar',
  FEATURED: 'Annons',
};

const PageSalesTool: FC = () => {
  const history = useHistory();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [orgOptions, setOrgOptions] = useState<{ label: string; value: string }[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const [supplierStats, setSupplierStats] = useState<any>(null);
  const [platformTotals, setPlatformTotals] = useState<any>(null);
  const [globalAreas, setGlobalAreas] = useState<any[]>([]);
  const [selectedAreaOptions, setSelectedAreaOptions] = useState<any[]>([]);
  const [selectedPlaceOptions, setSelectedPlaceOptions] = useState<any[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [combinedCategoryStats, setCombinedCategoryStats] = useState<any[]>([]);
  const [topCategories, setTopCategories] = useState<any[]>([]);
  const [geographyData, setGeographyData] = useState<any>(null);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [trendYear, setTrendYear] = useState<number>(new Date().getFullYear());
  const [supplierUsers, setSupplierUsers] = useState<any[]>([]);
  const [reachData, setReachData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Selector options
  const areaOptions = useMemo(
    () => globalAreas.map((a: any) => ({ label: a.name, value: a._id })),
    [globalAreas]
  );

  const selectedAreaIds = useMemo(
    () => selectedAreaOptions.map((o: any) => o.value),
    [selectedAreaOptions]
  );
  const selectedPlaces = useMemo(() => {
    const values = selectedPlaceOptions.map((o: any) => o.value);
    // "Hela Sverige" = no geographic filter; empty array tells backend to skip place filtering
    if (values.includes('Hela Sverige')) return [];
    return values;
  }, [selectedPlaceOptions]);

  // Load superadmin global categories and organization list on mount
  useEffect(() => {
    const loadAreas = async () => {
      try {
        const areas = await ServiceSalesTool.getGlobalCategories();
        setGlobalAreas(areas || []);
      } catch (err) {
        console.error('Failed to load global categories:', err);
      }
    };
    const loadOrgs = async () => {
      try {
        const orgs = await ServiceSalesTool.getOrganizations();
        setOrgOptions((orgs || []).map((org: any) => ({
          label: `${org.name} — ${org.organizationNumber || ''}`,
          value: org.organizationNumber || org._id,
        })));
      } catch (err) {
        console.error('Failed to load organizations:', err);
      }
    };
    loadAreas();
    loadOrgs();
    loadPlatformTotals();
    loadGeography();
    loadTopCategories();
  }, []);

  const loadPlatformTotals = async () => {
    try {
      const totals = await ServiceSalesTool.getPlatformTotals();
      setPlatformTotals(totals);
    } catch (err) {
      console.error('Failed to load platform totals:', err);
    }
  };

  const loadGeography = async () => {
    try {
      const geo = await ServiceSalesTool.getGeography();
      setGeographyData(geo);
    } catch (err) {
      console.error('Failed to load geography:', err);
    }
  };

  const loadTopCategories = async () => {
    try {
      const top = await ServiceSalesTool.getTopCategories({ limit: 20 });
      setTopCategories(top);
    } catch (err) {
      console.error('Failed to load top categories:', err);
    }
  };

  // Search handler — accepts query directly to avoid stale closure issues
  const handleSearch = useCallback(async (query?: string) => {
    const q = (query || searchQuery).trim();
    if (!q) return;
    setSearchLoading(true);
    setCompanyData(null);
    setSupplierStats(null);

    try {
      // Syna lookup uses raw query; supplier stats needs normalized org number (no dashes/spaces)
      const [company, stats] = await Promise.all([
        ServiceSalesTool.companyLookup(q).catch(() => null),
        ServiceSalesTool.getSupplierStats(q.replace(/[-\s]/g, '')).catch(() => null),
      ]);
      setCompanyData(company);
      setSupplierStats(stats);

      if (stats?.organizationNumber) {
        loadPrioData(stats.organizationNumber, stats.categories || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const loadPrioData = async (orgNum: string, areaIds: string[]) => {
    try {
      const [users, reach] = await Promise.all([
        ServiceSalesTool.getSupplierUsers(orgNum).catch(() => []),
        ServiceSalesTool.getSupplierReach(orgNum, areaIds).catch(() => null),
      ]);
      setSupplierUsers(users);
      setReachData(reach);
    } catch (err) {
      console.error('Failed to load prio data:', err);
    }
  };

  // Load combined category stats when filters change
  useEffect(() => {
    if (selectedAreaIds.length === 0) {
      setCombinedCategoryStats([]);
      setTrendData([]);
      return;
    }
    setStatsLoading(true);
    const dateRange = getDateRange(timeRange);
    const places = selectedPlaces.length > 0 ? selectedPlaces : undefined;

    Promise.all([
      ServiceSalesTool.getCategoryStatsCombined(selectedAreaIds, {
        places,
        ...dateRange,
        ongoingOnly: timeRange === 'today',
      }),
      ServiceSalesTool.getTrend({
        areaIds: selectedAreaIds,
        period: 'monthly',
        places,
      }),
    ])
      .then(([cats, trend]) => {
        setCombinedCategoryStats(cats);
        setTrendData(trend);
        // Default to latest year with data
        if (trend.length > 0) {
          const maxYear = Math.max(...trend.map((d: any) => d.year));
          setTrendYear(maxYear);
        }
      })
      .catch(err => console.error('Failed to load category stats:', err))
      .finally(() => setStatsLoading(false));
  }, [selectedAreaIds, selectedPlaces, timeRange]);

  // Memoized bid request count map by org name
  const bidRequestMap = useMemo(() => {
    const map = new Map<string, number>();
    (supplierStats?.bidRequestOrgs || []).forEach((org: any) => {
      map.set(org.orgName, org.count);
    });
    return map;
  }, [supplierStats]);

  // Memoized user grouping by email
  const groupedUsers = useMemo(() => {
    if (!supplierUsers || supplierUsers.length === 0) return [];
    const grouped = new Map<string, { name: string; email: string; title: string; phones: string[]; orgs: string[]; totalRequests: number }>();
    for (const u of supplierUsers) {
      const key = (u.email || '').toLowerCase();
      if (!grouped.has(key)) {
        grouped.set(key, { name: u.name, email: u.email, title: u.title || '', phones: [], orgs: [], totalRequests: 0 });
      }
      const g = grouped.get(key)!;
      g.totalRequests += u.requestCount || 0;
      if (u.orgName && !g.orgs.includes(u.orgName)) g.orgs.push(u.orgName);
      if (u.phone && !g.phones.includes(u.phone)) g.phones.push(u.phone);
      if (!g.title && u.title) g.title = u.title;
    }
    return Array.from(grouped.values());
  }, [supplierUsers]);

  const companyInfo = useMemo(() => {
    const info = parseSynaCompanyData(companyData);
    if (info && companyData?.boardData) {
      info.board = parseSynaBoardData(companyData.boardData);
    }
    return info;
  }, [companyData]);
  const ratingInfo = useMemo(() => parseSynaRating(companyData), [companyData]);
  const kpiData = useMemo(() => parseSynaKPIs(companyData), [companyData]);

  return (
    <AppLayout title="Säljverktyg">
      <div className="sales-tool">
        {/* Search Section — creatable dropdown with all org accounts + free text */}
        <div className="sales-tool__search-section">
          <div className="sales-tool__search-input">
            <Selector
              isCreatable
              isClearable
              isSearchable
              placeholder="Sök på organisationsnummer eller företagsnamn..."
              options={orgOptions}
              value={selectedOrg}
              formatCreateLabel={(input: string) => `Sök "${input}"...`}
              onChange={(option: any) => {
                setSelectedOrg(option);
                if (!option) {
                  // Cleared
                  setCompanyData(null);
                  setSupplierStats(null);
                  setSearchQuery('');
                  return;
                }
                const q = option.value;
                setSearchQuery(q);
                handleSearch(q);
              }}
              filterOption={createFilter({ ignoreAccents: false })}
            />
          </div>
        </div>

        {/* === Allmän information om Accurator === */}
        <div className="sales-tool__section">
          <h3 className="sales-tool__section-title">Allmän information om Accurator</h3>
          <div className="sales-tool__platform-grid">
            {[
              { label: 'Inköpskunder (totalt)', value: platformTotals?.purchasingCustomers || 0 },
              { label: 'Leverantörskunder (totalt)', value: platformTotals?.salesCustomers || 0 },
              { label: 'Prio-medlemskap (aktiva)', value: platformTotals?.prioSubscriptions || 0 },
              { label: 'Publicerade förfrågningar (aktiva abonnemang)', value: platformTotals?.publishedSubscriptions || 0 },
              { label: 'Pågående upphandlingar', value: platformTotals?.ongoingProcurements || 0 },
              { label: 'Upphandlingar (totalt)', value: platformTotals?.totalProcurements || 0 },
              { label: 'Total upphandlad volym', value: platformTotals?.totalVolume || 0, isMSEK: true },
              { label: 'Unika leverantörer (totalt)', value: platformTotals?.totalUniqueSuppliers || 0 },
            ].map((item: any) => (
              <div key={item.label} className="sales-tool__platform-tile">
                <div className="sales-tool__platform-tile-value">
                  {item.isMSEK ? (
                    <>{Math.round(item.value / 1000000).toLocaleString('sv-SE')} <span className="sales-tool__platform-tile-unit">MSEK</span></>
                  ) : (
                    typeof item.value === 'number' ? item.value.toLocaleString('sv-SE') : item.value
                  )}
                </div>
                <div className="sales-tool__platform-tile-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Company Info Card */}
        {companyInfo && (
          <div className="sales-tool__company-card">
            <div className="sales-tool__company-card-header">
              <span className="sales-tool__company-card-name">{companyInfo.name}</span>
              <div className="sales-tool__company-card-badges">
                {supplierStats?.accountTypes?.includes('SUPERADMIN') && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--superadmin">Superadmin</span>
                )}
                {supplierStats?.accountTypes?.includes('PURCHASING') && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--purchasing">Inköp</span>
                )}
                {supplierStats?.accountTypes?.includes('SALES') && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--sales">Leverantör</span>
                )}
                {supplierStats?.subscriptions?.map((sub: any) => (
                  <span key={sub.product} className="sales-tool__company-card-badge sales-tool__company-card-badge--subscription">
                    {subscriptionLabels[sub.product] || sub.product}
                  </span>
                ))}
                {!supplierStats?.accountTypes?.filter((t: string) => t !== 'SUPERADMIN').length &&
                  !supplierStats?.accountTypes?.includes('SUPERADMIN') && supplierStats && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--none">Ej konto</span>
                )}
              </div>
            </div>
            <div className="sales-tool__company-card-grid">
              <div className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">Org.nummer</div>
                <div className="sales-tool__company-card-field-value">{companyInfo.orgNumber}</div>
              </div>
              <div className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">Adress</div>
                <div className="sales-tool__company-card-field-value">
                  {companyInfo.address}, {companyInfo.postcode} {companyInfo.city}
                </div>
              </div>
              <div className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">Registreringsdatum</div>
                <div className="sales-tool__company-card-field-value">
                  {companyInfo.regdatum?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                </div>
              </div>
              {companyInfo.bransch && (
                <div className="sales-tool__company-card-field">
                  <div className="sales-tool__company-card-field-label">Bransch</div>
                  <div className="sales-tool__company-card-field-value">{companyInfo.bransch}</div>
                </div>
              )}
              {ratingInfo && (
                <div className="sales-tool__company-card-field">
                  <div className="sales-tool__company-card-field-label">Kreditlimit</div>
                  <div className="sales-tool__company-card-field-value">
                    {Number(ratingInfo.limit).toLocaleString('sv-SE')} kr
                  </div>
                </div>
              )}
            </div>

            {/* Board / Ledning & Styrelse */}
            {companyInfo.board?.length > 0 && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">Ledning & Styrelse</h4>
                <div className="sales-tool__company-card-grid">
                  {companyInfo.board.map((member: any, i: number) => (
                    <div key={i} className="sales-tool__company-card-field">
                      <div className="sales-tool__company-card-field-label">{member.role}</div>
                      <div className="sales-tool__company-card-field-value">{member.name}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Bokslut / Financial Statements */}
            {kpiData.length > 0 && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">Bokslut</h4>
                <div className="sales-tool__bokslut">
                  <div className="sales-tool__bokslut-chart">
                    <BarChart
                      title=""
                      data={[...kpiData].reverse().map(k => ({
                        name: k.period,
                        'Omsättning': k.omsattning,
                        'Resultat': k.resultat,
                      }))}
                      index="name"
                      categories={['Omsättning', 'Resultat']}
                      colors={['emerald', 'amber']}
                      unit={UNIT.AMOUNT}
                    />
                  </div>
                  <div className="sales-tool__bokslut-table">
                    <div className="sales-tool__bokslut-header">
                      <span>Bokslut</span>
                      <span>{kpiData[0]?.period}</span>
                    </div>
                    {[
                      { label: 'Omsättning', value: kpiData[0]?.omsattning, highlight: true },
                      { label: 'Resultat efter finansnetto', value: kpiData[0]?.resultat, highlight: true },
                      { label: 'Summa tillgångar', value: kpiData[0]?.balansomslutning },
                      { label: 'Eget kapital', value: kpiData[0]?.egetKapital, highlight: true },
                      { label: 'Anställda', value: kpiData[0]?.anstallda, isCount: true },
                      { label: 'Soliditet', value: kpiData[0]?.soliditet, isSuffix: '%' },
                      { label: 'Kassalikviditet', value: kpiData[0]?.kassalikviditet, isSuffix: '%' },
                    ].map((row, i) => (
                      <div key={i} className={`sales-tool__bokslut-row ${row.highlight ? 'sales-tool__bokslut-row--highlight' : ''}`}>
                        <span>{row.label}</span>
                        <span>
                          {row.isSuffix
                            ? `${row.value}${row.isSuffix}`
                            : row.isCount
                              ? row.value
                              : `${Number(row.value).toLocaleString('sv-SE')} tkr`}
                        </span>
                      </div>
                    ))}
                    <div className="sales-tool__bokslut-row sales-tool__bokslut-row--footer">
                      <span>Valutakod (Belopp i 1000)</span>
                      <span>SEK</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Supplier Stats inside company card */}
            {supplierStats && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">Aktivitet på Accurator</h4>
                <div className="sales-tool__company-card-stats">
                  {[
                    {
                      value: supplierStats.customerRegisters?.count || 0,
                      label: 'Kundregister',
                      tooltip: 'Antal kundorganisationer som har denna leverantör i sitt leverantörsregister på Accurator.',
                    },
                    {
                      value: reachData?.currentCustomers ?? '-',
                      label: 'Kunder idag',
                      tooltip: 'Antal unika kundorganisationer som har skickat förfrågningar till denna leverantör.',
                    },
                    {
                      value: reachData?.totalPotential ?? '-',
                      label: 'Total potential',
                      tooltip: 'Antal unika kundorganisationer som har upphandlingar i leverantörens kategorier. Visar hur många kunder leverantören potentiellt kan nå.',
                    },
                    {
                      value: supplierStats.bidRequests?.received || 0,
                      label: 'Mottagna förfrågningar',
                      tooltip: 'Totalt antal förfrågningar som har skickats till denna leverantör via Accurator.',
                    },
                    {
                      value: supplierStats.bidRequests?.submitted || 0,
                      label: 'Inlämnade anbud',
                      tooltip: 'Antal anbud som leverantören har lämnat in som svar på förfrågningar.',
                    },
                    {
                      value: supplierStats.wonDeals || 0,
                      label: 'Vunna affärer',
                      tooltip: 'Antal upphandlingar där leverantören har blivit tilldelad uppdraget.',
                    },
                    {
                      value: supplierStats.users?.length || 0,
                      label: 'Kontakter',
                      tooltip: 'Antal användare som är registrerade på leverantörens konto i Accurator.',
                    },
                  ].map(stat => (
                    <TooltipContainer
                      key={stat.label}
                      renderReferenceComponent={(className, ref) => (
                        <div className={`sales-tool__company-card-stat ${className}`} ref={ref}>
                          <span className="sales-tool__company-card-stat-value">{stat.value}</span>
                          <span className="sales-tool__company-card-stat-label">{stat.label}</span>
                        </div>
                      )}
                    >
                      <div style={{ maxWidth: 260, fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                        <strong style={{ display: 'block', marginBottom: 4 }}>{stat.label}</strong>
                        {stat.tooltip}
                      </div>
                    </TooltipContainer>
                  ))}
                </div>
              </>
            )}

            {/* Supplier Controls inside company card */}
            {supplierStats?.organizationNumber && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">Leverantörskontroller</h4>
                <div className="sales-tool__company-card-controls">
                  <SupplierControls
                    organizationNumber={supplierStats.organizationNumber}
                    companyName={supplierStats.name || companyInfo?.name}
                    hasSyna={true}
                    hasCreditsafe={false}
                    hasTic={false}
                    history={history}
                  />
                </div>
              </>
            )}

            {/* Customer Organizations + Users + Bid Request Orgs */}
            {supplierStats && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">
                  Organisationer med leverantören i sitt register ({supplierStats.customerRegisters?.count || 0})
                </h4>

                <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Organisation</th>
                        <th>Status</th>
                        <th>Antal förfrågningar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierStats.customerRegisters?.organizations?.length > 0 ? (
                        supplierStats.customerRegisters.organizations.map((org: any, i: number) => {
                          const status = org.prequalification || 'APPROVED';
                          const label = status === 'APPROVED' ? 'Godkänd' : status === 'REJECTED' ? 'Nekad' : 'Ansökan';
                          const badgeClass = status === 'APPROVED' ? 'sales-tool__status-badge--approved'
                            : status === 'REJECTED' ? 'sales-tool__status-badge--rejected'
                            : 'sales-tool__status-badge--pending';
                          const bidCount = bidRequestMap.get(org.orgName);
                          return (
                            <tr key={i}>
                              <td>{org.orgName}</td>
                              <td><span className={`sales-tool__status-badge ${badgeClass}`}>{label}</span></td>
                              <td>{bidCount != null ? `${bidCount} st` : '–'}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                          Inga organisationer har denna leverantör i sitt register.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Users as subsection — grouped by email */}
                <h4 className="sales-tool__company-card-subtitle">
                  Användare ({groupedUsers.length})
                </h4>
                <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Namn</th>
                        <th>E-post</th>
                        <th>Telefon</th>
                        <th>Titel</th>
                        <th>Förfrågningar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedUsers.length > 0 ? (
                        groupedUsers.map((user, i) => (
                          <TooltipContainer
                            key={i}
                            renderReferenceComponent={(className, ref) => (
                              <tr className={className} ref={ref}>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>
                                  {user.phones[0] || '-'}
                                  {user.phones.length > 1 && (
                                    <TooltipContainer
                                      renderReferenceComponent={(cn, r) => (
                                        <span className={cn} ref={r} style={{ marginLeft: 4, cursor: 'help', verticalAlign: 'middle' }}>
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                        </span>
                                      )}
                                    >
                                      <div style={{ fontSize: 13, padding: '4px 0' }}>
                                        <strong>Övriga telefonnummer:</strong>
                                        {user.phones.slice(1).map((p, j) => <div key={j}>{p}</div>)}
                                      </div>
                                    </TooltipContainer>
                                  )}
                                </td>
                                <td>{user.title || '-'}</td>
                                <td>{user.totalRequests} st</td>
                              </tr>
                            )}
                          >
                            {user.orgs.length > 0 && (
                              <div style={{ fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                                <strong>{user.orgs.length === 1 ? 'Organisation:' : 'Organisationer:'}</strong>
                                {user.orgs.map((org, j) => <div key={j}>{org}</div>)}
                              </div>
                            )}
                          </TooltipContainer>
                        ))
                      ) : (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                          Inga registrerade användare hos leverantören.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Show orgs that sent bids but are NOT in the register */}
                {supplierStats.bidRequestOrgs?.some((org: any) =>
                  !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName)
                ) && (
                  <div className="sales-tool__supplier-users" style={{ marginTop: -8 }}>
                    <table>
                      <thead>
                        <tr>
                          <th colSpan={3} style={{ fontWeight: 500, fontSize: 13, color: 'var(--gray-500)', paddingTop: 4 }}>
                            Övriga organisationer som skickat förfrågningar
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierStats.bidRequestOrgs
                          .filter((org: any) => !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName))
                          .map((org: any, i: number) => (
                            <tr key={i}>
                              <td>{org.orgName}</td>
                              <td>–</td>
                              <td>{org.count} st</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Category & Place Filters with Selector */}
        <div className="sales-tool__section">
          <h3 className="sales-tool__section-title">Upphandlingar per kategori</h3>

          <div className="selector-row">
            <div>
              <Selector
                isClearable
                isMulti
                isOverflow
                placeholder="Kategorier"
                options={areaOptions}
                value={selectedAreaOptions}
                onChange={(e: any) => setSelectedAreaOptions(e || [])}
                filterOption={createFilter({ ignoreAccents: false })}
              />
              {supplierStats?.supplierProfile?.areas?.length > 0 && (() => {
                // Map supplier's org-specific area IDs to global categories via customerAreas
                const supplierGlobalMatches = supplierStats.supplierProfile.areas
                  .map((area: any) => {
                    if (!area?._id) return null;
                    const globalMatch = globalAreas.find((g: any) =>
                      g.customerAreas?.some((ca: any) => ca.areaId === area._id)
                    );
                    return globalMatch ? { globalId: globalMatch._id, globalName: globalMatch.name } : null;
                  })
                  .filter(Boolean)
                  // Deduplicate by global ID
                  .filter((v: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.globalId === v.globalId) === i);

                if (!supplierGlobalMatches.length) return null;
                return (
                  <div className="sales-tool__profile-tags">
                    <span className="sales-tool__profile-tags-label">Leverantörens:</span>
                    {supplierGlobalMatches.map((match: any) => {
                      const option = areaOptions.find((o: any) => o.value === match.globalId);
                      if (!option) return null;
                      const isSelected = selectedAreaOptions.some((o: any) => o.value === match.globalId);
                      return (
                        <button
                          key={match.globalId}
                          className={`sales-tool__profile-tag ${isSelected ? 'sales-tool__profile-tag--active' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedAreaOptions(selectedAreaOptions.filter((o: any) => o.value !== match.globalId));
                            } else {
                              setSelectedAreaOptions([...selectedAreaOptions, option]);
                            }
                          }}
                        >
                          {match.globalName}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div>
              <Selector
                isClearable
                isMulti
                isOverflow
                placeholder="Verksamhetsområde"
                options={placeOptions}
                value={selectedPlaceOptions}
                onChange={(e: any) => setSelectedPlaceOptions(e || [])}
                filterOption={createFilter({ ignoreAccents: false })}
              />
              {supplierStats?.supplierProfile?.places?.length > 0 && (
                <div className="sales-tool__profile-tags">
                  <span className="sales-tool__profile-tags-label">Leverantörens:</span>
                  {supplierStats.supplierProfile.places.map((place: string) => {
                    const option = placeOptions.find((o: any) => o.value === place) || { label: place, value: place };
                    const isSelected = selectedPlaceOptions.some((o: any) => o.value === place);
                    return (
                      <button
                        key={place}
                        className={`sales-tool__profile-tag ${isSelected ? 'sales-tool__profile-tag--active' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedPlaceOptions(selectedPlaceOptions.filter((o: any) => o.value !== place));
                          } else {
                            setSelectedPlaceOptions([...selectedPlaceOptions, option]);
                          }
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="sales-tool__filters">
            <div className="sales-tool__filters-group">
              <label>Tidsperiod</label>
              <div className="sales-tool__toggle">
                {(Object.keys(timeRangeLabels) as TimeRange[]).map(key => (
                  <button
                    key={key}
                    className={`sales-tool__toggle-btn ${timeRange === key ? 'sales-tool__toggle-btn--active' : ''}`}
                    onClick={() => setTimeRange(key)}
                  >
                    {timeRangeLabels[key]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Combined Category Stats Chart */}
          {statsLoading && <div className="sales-tool__loading">Laddar statistik...</div>}
          {!statsLoading && selectedAreaIds.length === 0 && (
            <Card style={{ padding: '48px 32px', textAlign: 'center', border: '1px dashed var(--gray-300)', boxShadow: 'none' }}>
              <Text style={{ color: 'var(--gray-400)', fontSize: 14 }}>Välj kategori och verksamhetsområde för att visa data</Text>
            </Card>
          )}
          {!statsLoading && selectedAreaIds.length > 0 && combinedCategoryStats.length === 0 && (
            <Card style={{ padding: '32px', textAlign: 'center' }}>
              <Text style={{ color: 'var(--gray-500)' }}>Ingen data för vald kategori</Text>
            </Card>
          )}
          {!statsLoading && combinedCategoryStats.length > 0 && (
            <BarChart
              title="Upphandlingar per kategori (publicerade vs ej publicerade)"
              index="areaName"
              data={combinedCategoryStats.map(c => ({
                areaName: c.areaName || 'Okänd',
                Publicerade: c.publicCount || 0,
                'Ej publicerade': c.privateCount || 0,
              }))}
              categories={['Publicerade', 'Ej publicerade']}
              colors={['orange', 'cyan']}
            />
          )}

          {/* Trend — inside Upphandlingar per kategori section */}
          {trendData.length > 0 && (() => {
            const years = [...new Set(trendData.map(d => d.year).filter(Boolean))].sort((a, b) => b - a);
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
            const filtered = trendData.filter(d => d.year === trendYear);
            return (
              <div style={{ marginTop: 16 }}>
                <div className="sales-tool__section-header" style={{ marginBottom: 8 }}>
                  <div />
                  <div className="sales-tool__toggle">
                    {years.map(y => (
                      <button
                        key={y}
                        className={`sales-tool__toggle-btn ${trendYear === y ? 'sales-tool__toggle-btn--active' : ''}`}
                        onClick={() => setTrendYear(y)}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
                <BarChart
                  title="Upphandlingar över tid (för valda kategorier)"
                  index="period"
                  data={monthNames.map((name, i) => {
                    const match = filtered.find(d => d.month === i + 1);
                    return { period: name, Antal: match?.count || 0 };
                  })}
                  categories={['Antal']}
                  colors={['orange']}
                />
              </div>
            );
          })()}
        </div>

        {/* Top Categories & Geography */}
        <div className="sales-tool__section">
          <div className="sales-tool__chart-grid">
            <SummaryBarList
              title="Mest efterfrågade kategorier"
              infoText="Visar de kategorier som har flest upphandlingar på plattformen. Kategorier i orange är globala (superadmin), gråa kategorier saknar koppling till en global kategori."
              data={topCategories.map(c => {
                const isGlobal = globalAreas.some((g: any) => g._id === c.areaId);
                return {
                  name: c.areaName || 'Okänd',
                  value: c.count,
                  ...(!isGlobal ? { color: 'gray' as const } : {}),
                };
              })}
              keyColumnName="Kategori"
              valueColumName="Antal"
              maxItems={10}
            />
            {geographyData && (
              <SummaryBarList
                title="Accurator kunder per region"
                infoText="Antal kundorganisationer med inköpskonto per region. Baserat på organisationens registrerade kommun."
                data={(geographyData.byRegion || []).map((r: any) => ({
                  name: r.region,
                  value: r.count,
                }))}
                keyColumnName="Region"
                valueColumName="Antal kunder"
                maxItems={10}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default PageSalesTool;
